import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, LoaderCircle, ShieldCheck } from 'lucide-react';
import * as THREE from 'three';
import type { UniversalBundle } from '@/shared/api/types';
import { ArButton } from '@/features/scene/ArButton';
import { useThreeScene, applyProjection, type ThreeCtx } from '@/features/scene/hooks/useThreeScene';
import { useGltfLoader, type SceneBoundsPayload } from '@/features/scene/hooks/useGltfLoader';
import { useLighting } from '@/features/scene/hooks/useLighting';
import { useHdri } from '@/features/scene/hooks/useHdri';
import { useRenderLoop, type LoopHook } from '@/features/scene/hooks/useRenderLoop';
import { bundleFrontCameraZ } from '@/features/scene/lib/geometry-helpers';
import { PublicNativeCanvas, parsePublicNative, type PublicNativeDescriptor } from './PublicNativeCanvas';

type ShareResponse = {
  ok: boolean;
  model: { id: string; name: string };
  expiresAt: string;
  bundle?: UniversalBundle;
  nativeModel?: PublicNativeDescriptor;
};

export function publicShareToken(): string | null {
  if (typeof window === 'undefined') return null;
  const value = new URLSearchParams(window.location.hash.slice(1)).get('share')?.trim() || '';
  return value.length >= 32 ? value : null;
}

/**
 * Isolated public renderer.  It deliberately does not mount App/AuthGate or
 * SceneCanvas: neither protected queries nor edit controls exist in this tree.
 */
export function PublicShareView() {
  const tokenRef = useRef(publicShareToken());
  const [state, setState] = useState<{ value?: ShareResponse; error?: string; loading: boolean }>({ loading: true });

  useEffect(() => {
    const token = tokenRef.current;
    // Keep the client-only fragment so reload/reopen retains the capability.
    // It is never sent in the request URL; all public fetches suppress referrers.
    if (!token) {
      setState({ loading: false, error: 'Ссылка на просмотр некорректна или неполна.' });
      return;
    }
    let cancelled = false;
    const abort = new AbortController();
    void fetch('/api/public/model-share', {
      headers: { 'X-Holzcom-Share': token },
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      signal: abort.signal,
      cache: 'no-store',
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(response.status === 401 ? 'Ссылка недействительна или истекла.' : `Ошибка загрузки (${response.status})`);
        return response.json() as Promise<ShareResponse>;
      })
      .then((value) => {
        if (!value || value.ok !== true || !value.model || typeof value.model.id !== 'string') throw new Error('Некорректный ответ модели.');
        if (value.nativeModel) {
          if (value.bundle) throw new Error('Неоднозначный формат модели.');
          value.nativeModel = parsePublicNative(value.nativeModel, value.model.id);
        } else if (!value.bundle) throw new Error('Нет данных модели.');
        if (!cancelled) setState({ value, loading: false });
      })
      .catch((error: unknown) => { if (!cancelled) setState({ loading: false, error: error instanceof Error ? error.message : 'Не удалось открыть модель.' }); });
    return () => { cancelled = true; abort.abort(); };
  }, []);

  if (state.loading) return <PublicShell><div className="flex items-center gap-2 text-sm text-fg-muted"><LoaderCircle className="h-4 w-4 animate-spin" /> Загружаю модель…</div></PublicShell>;
  if (state.error || !state.value) return <PublicShell><div className="max-w-md rounded-lg border border-err/40 bg-surface px-5 py-4 text-sm text-fg"><div className="font-medium text-err">Просмотр недоступен</div><div className="mt-1 text-fg-muted">{state.error || 'Нет данных модели.'}</div></div></PublicShell>;
  return <PublicModelScene value={state.value} token={tokenRef.current || ''} />;
}

function PublicShell({ children }: { children: React.ReactNode }) {
  return <main className="grid h-screen w-screen place-items-center bg-bg text-fg">{children}</main>;
}

function PublicModelScene({ value, token }: { value: ShareResponse; token: string }) {
  return (
    <main className="h-screen w-screen overflow-hidden bg-bg text-fg">
      <header className="absolute left-0 right-0 top-0 z-10 flex h-12 items-center justify-between border-b border-border bg-surface/95 px-4 shadow-sm backdrop-blur">
        <div className="flex min-w-0 items-center gap-2">
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded bg-accent/15 text-accent"><Box className="h-3.5 w-3.5" /></span>
          <span className="text-sm font-semibold">HOLZCOM</span>
          <span className="truncate text-sm text-fg-muted">· {value.model.name}</span>
        </div>
        <span className="hidden items-center gap-1.5 text-xs text-fg-subtle sm:flex"><ShieldCheck className="h-3.5 w-3.5" /> Только просмотр</span>
      </header>
      {value.nativeModel ? <PublicNativeCanvas descriptor={value.nativeModel} token={token} />
        : value.bundle ? <PublicSceneCanvas modelId={value.model.id} bundle={value.bundle} /> : null}
    </main>
  );
}

function PublicSceneCanvas({ modelId, bundle }: { modelId: string; bundle: UniversalBundle }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const ctx = useThreeScene(containerRef);
  const [bounds, setBounds] = useState<{ center: THREE.Vector3; size: THREE.Vector3 } | null>(null);
  const onBoundsReady = useCallback((next: SceneBoundsPayload) => setBounds({ center: next.center, size: next.size }), []);
  const loader = useGltfLoader(ctx, modelId, bundle, 'tech', onBoundsReady);
  useLighting(ctx, 'showroom', 1);
  useHdri(ctx, 'neutral', 0, 1, false);
  const loopHooks = useMemo<LoopHook[]>(() => [], []);
  useRenderLoop(ctx, loopHooks);

  useEffect(() => {
    if (!ctx || !bounds) return;
    applyProjection(ctx, 'perspective', bounds, bundleFrontCameraZ(bundle));
  }, [ctx, bounds, bundle]);

  return (
    <div className="relative h-full w-full pt-12">
      <div ref={containerRef} className="absolute inset-0" />
      <div className="absolute bottom-3 left-3 rounded-md border border-border bg-surface/90 px-2.5 py-1 text-xs text-fg-muted shadow-hud">
        {loader.status === 'error' ? <span role="alert">{loader.error || 'Не удалось открыть модель.'}</span>
          : loader.status === 'ready' ? 'Потяните мышью для обзора · колесо — масштаб' : 'Собираю 3D‑сцену…'}
      </div>
      {/* AR «поставить у себя» (волна 3): публичная ссылка открывается с телефона,
          WebXR-сессия переиспользует эту же сцену; кнопка появляется только на
          AR-устройствах (useArSession сам скрывается на десктопе). */}
      <ArButton ctx={ctx} />
    </div>
  );
}
